import { z } from 'zod';

import { BINARY_ANSWERS, type BinaryAnswer } from '@/constants/answers';
import { BeneficialOwnerType, BUSINESS_TYPES, BusinessType, ManagementRole, type OwnershipType, REGULATED_MARKET_LISTINGS, RegulatedMarketListing } from '@/constants/institution';
import {
  INSTITUTION_ADDRESS_DEFAULTS,
  type InstitutionAddress,
  institutionAddressSchema,
  institutionAddressToInstitutionDetailsAddressApi,
  institutionDetailsAddressApiToInstitutionAddress,
} from '@/features/institution/schemas/address';
import {
  type InstitutionPerson,
  institutionDetailsPersonApiToInstitutionPerson,
  institutionPersonSchema,
  institutionPersonToInstitutionDetailsPersonApi,
} from '@/features/institution/schemas/person';
import i18n from '@/lib/i18n';
import {
  createInstitutionDetailsV2ManagementSchema,
  hasListedCompanyIdentifier,
  hasManagementRole,
  type InstitutionDetailsV2CorporateOwner,
  type InstitutionDetailsV2ManagementPerson,
  type InstitutionDetailsV2Ownership,
  institutionDetailsV2BeneficialOwnerSchema,
  institutionDetailsV2CorporateOwnerBaseSchema,
  institutionDetailsV2CorporateOwnerTypeOfBusinessOtherSchema,
  institutionDetailsV2ListedCompanyNameSchema,
  institutionDetailsV2ListedCompanySchema,
  institutionDetailsV2ManagementPersonSchema,
  institutionDetailsV2ManagementRolesSchema,
  institutionDetailsV2OwnershipSchema,
  institutionDetailsV2UboOwnershipBaseSchema,
} from '@/services/api/profile';
import { type DateParts, EMPTY_DATE_PARTS, isDatePartsValid, toDatePartsFromString, toDateStringFromParts } from '@/utils/date';
import { addFieldIssue } from '@/utils/field-issues';

type InstitutionCorporateOwner = {
  partyId: string;
  companyName: string;
  registrationNumber: string;
  dateOfIncorporation: DateParts;
  countryOfIncorporation: string;
  registeredAddress: InstitutionAddress;
  taxId: string;
  leiNumber: string;
  website: string;
  ownershipPct?: number;
  typeOfBusiness?: BusinessType;
  typeOfBusinessOther: string;
};

type InstitutionBeneficialOwner = InstitutionPerson & {
  ownershipPct?: number;
  isPep?: BinaryAnswer;
};

type InstitutionListedCompany = {
  // Only asked of a wholly owned subsidiary, about its listed parent
  name: string;
  mic: string;
  ticker: string;
  isin: string;
};

export type InstitutionManagementPerson = InstitutionPerson & {
  roles: ManagementRole[];
  isPep?: BinaryAnswer;
};

export type InstitutionOwnership = {
  regulatedMarketListing?: RegulatedMarketListing;
  listedCompany: InstitutionListedCompany;
  ownershipType?: OwnershipType;
  bearerSharesPct?: number;
  ownershipChart?: string;
  hasCorporateOwners?: BinaryAnswer;
  corporateOwners: InstitutionCorporateOwner[];
  // Not saved: whether the beneficial owners are owners or a senior managing official
  hasOwners?: BinaryAnswer;
  ubos: InstitutionBeneficialOwner[];
  management: InstitutionManagementPerson[];
};

export type InstitutionOwnershipOrDefaults = InstitutionOwnership | Partial<InstitutionOwnership>;

const INSTITUTION_LISTED_COMPANY_DEFAULTS: InstitutionListedCompany = { name: '', mic: '', ticker: '', isin: '' };

export const INSTITUTION_OWNERSHIP_DEFAULTS: InstitutionOwnership = { listedCompany: INSTITUTION_LISTED_COMPANY_DEFAULTS, corporateOwners: [], ubos: [], management: [] };

const isOwnershipPctTotalValid = (owners: Array<{ ownershipPct: number }>) => owners.reduce((total, owner) => total + owner.ownershipPct, 0) <= 100;

export const isListedOwnership = (regulatedMarketListing: RegulatedMarketListing | undefined) =>
  regulatedMarketListing === RegulatedMarketListing.LISTED || regulatedMarketListing === RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY;

const institutionCorporateOwnerSchema = z
  .object({
    partyId: institutionDetailsV2CorporateOwnerBaseSchema.shape.partyId,
    companyName: institutionDetailsV2CorporateOwnerBaseSchema.shape.companyName,
    registrationNumber: institutionDetailsV2CorporateOwnerBaseSchema.shape.registrationNumber,
    dateOfIncorporation: z
      .object({
        day: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.corporateOwner.errors.dateOfIncorporation')),
        month: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.corporateOwner.errors.dateOfIncorporation')),
        year: z.string().regex(/^\d{4}$/, i18n.t('institutionDetails.corporateOwner.errors.dateOfIncorporation')),
      })
      .refine(isDatePartsValid, { error: i18n.t('institutionDetails.corporateOwner.errors.dateOfIncorporation'), when: ({ issues }) => issues.length === 0 }),
    countryOfIncorporation: institutionDetailsV2CorporateOwnerBaseSchema.shape.countryOfIncorporation,
    registeredAddress: institutionAddressSchema,
    taxId: institutionDetailsV2CorporateOwnerBaseSchema.shape.taxId,
    leiNumber: z.union([institutionDetailsV2CorporateOwnerBaseSchema.shape.leiNumber.unwrap(), z.literal('')], {
      error: i18n.t('institutionDetails.corporateOwner.errors.leiNumber'),
    }),
    website: z.union([institutionDetailsV2CorporateOwnerBaseSchema.shape.website.unwrap(), z.literal('')], {
      error: i18n.t('institutionDetails.corporateOwner.errors.website'),
    }),
    ownershipPct: institutionDetailsV2CorporateOwnerBaseSchema.shape.ownershipPct,
    typeOfBusiness: z.enum(BUSINESS_TYPES, { error: i18n.t('institutionDetails.corporateOwner.errors.typeOfBusiness') }),
    typeOfBusinessOther: z.string(),
  })
  .superRefine((corporateOwner, context) => {
    if (corporateOwner.typeOfBusiness === BusinessType.OTHER) {
      addFieldIssue(context, ['typeOfBusinessOther'], institutionDetailsV2CorporateOwnerTypeOfBusinessOtherSchema, corporateOwner.typeOfBusinessOther);
    }
  });

const [institutionDetailsV2BeneficialOwnerOwnerSchema, institutionDetailsV2BeneficialOwnerSeniorManagingOfficialSchema] = institutionDetailsV2BeneficialOwnerSchema.options;

// A blank identifier, spaces included, counts as not given, and the API leaves it out
const blankOr = (schema: z.ZodType<string>, error: string) => z.union([schema, z.string().trim().max(0)], { error });

const absentIfBlank = (identifier: string) => identifier.trim() || undefined;

const institutionListedCompanySchema = z.object({
  mic: blankOr(institutionDetailsV2ListedCompanySchema.shape.mic.unwrap(), i18n.t('institutionDetails.ownership.errors.mic')),
  ticker: blankOr(institutionDetailsV2ListedCompanySchema.shape.ticker.unwrap(), i18n.t('institutionDetails.ownership.errors.ticker')),
  isin: blankOr(institutionDetailsV2ListedCompanySchema.shape.isin.unwrap(), i18n.t('institutionDetails.ownership.errors.isin')),
});

// Either input answers it, so both show the error
const requireIsinOrTicker = (listedCompany: { ticker: string; isin: string }, context: z.RefinementCtx) => {
  if (hasListedCompanyIdentifier(listedCompany)) return;

  for (const key of ['isin', 'ticker']) {
    context.addIssue({ code: 'custom', path: [key], message: i18n.t('institutionDetails.ownership.errors.isinOrTicker') });
  }
};

export const institutionOwnershipListingSchema = z.discriminatedUnion(
  'regulatedMarketListing',
  [
    z.object({
      regulatedMarketListing: z.literal(RegulatedMarketListing.LISTED),
      // The form keeps a name on every route but only asks a subsidiary for it, so it goes unchecked here
      listedCompany: institutionListedCompanySchema.extend({ name: z.string() }).superRefine(requireIsinOrTicker),
    }),
    z.object({
      regulatedMarketListing: z.literal(RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY),
      listedCompany: institutionListedCompanySchema.extend({ name: institutionDetailsV2ListedCompanyNameSchema }).superRefine(requireIsinOrTicker),
      ownershipChart: institutionDetailsV2UboOwnershipBaseSchema.shape.ownershipChart,
    }),
    z.object({
      regulatedMarketListing: z.literal(RegulatedMarketListing.NONE),
    }),
  ],
  { error: i18n.t('institutionDetails.ownership.errors.regulatedMarketListing') },
);

export const institutionOwnershipStructureSchema = institutionDetailsV2UboOwnershipBaseSchema.pick({ ownershipType: true, bearerSharesPct: true, ownershipChart: true }).extend({
  hasCorporateOwners: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.ownership.errors.hasCorporateOwners') }),
});

export const institutionOwnershipCorporateOwnersSchema = z.object({
  corporateOwners: z
    .array(institutionCorporateOwnerSchema)
    .min(1, i18n.t('institutionDetails.ownership.errors.corporateOwners'))
    .refine(isOwnershipPctTotalValid, { error: i18n.t('institutionDetails.ownership.errors.ownershipPctTotal'), when: ({ issues }) => issues.length === 0 }),
});

export const institutionOwnershipUbosSchema = z.discriminatedUnion(
  'hasOwners',
  [
    z.object({
      hasOwners: z.literal('YES'),
      ubos: z
        .array(
          institutionPersonSchema.extend({
            ownershipPct: institutionDetailsV2BeneficialOwnerOwnerSchema.shape.ownershipPct,
            isPep: institutionDetailsV2BeneficialOwnerOwnerSchema.shape.isPep,
          }),
        )
        .min(1, i18n.t('institutionDetails.ownership.errors.ubos'))
        .refine(isOwnershipPctTotalValid, { error: i18n.t('institutionDetails.ownership.errors.ownershipPctTotal'), when: ({ issues }) => issues.length === 0 }),
    }),
    z.object({
      hasOwners: z.literal('NO'),
      ubos: z.array(institutionPersonSchema.extend({ isPep: institutionDetailsV2BeneficialOwnerSeniorManagingOfficialSchema.shape.isPep })).length(1),
    }),
  ],
  { error: i18n.t('institutionDetails.ownership.errors.hasOwners') },
);

export const institutionOwnershipManagementSchema = z
  .object({
    regulatedMarketListing: z.enum(REGULATED_MARKET_LISTINGS),
    management: createInstitutionDetailsV2ManagementSchema(
      institutionPersonSchema.extend({ roles: institutionDetailsV2ManagementRolesSchema, isPep: institutionDetailsV2ManagementPersonSchema.shape.isPep }),
    ),
  })
  .superRefine(({ regulatedMarketListing, management }, context) => {
    if (isListedOwnership(regulatedMarketListing) && !hasManagementRole(ManagementRole.SENIOR_MANAGING_OFFICIAL)(management)) {
      context.addIssue({ code: 'custom', path: ['management'], message: i18n.t('institutionDetails.ownership.errors.managementSeniorManagingOfficialRequired') });
    }
  });

export const createInstitutionCorporateOwner = (): InstitutionCorporateOwner => ({
  partyId: crypto.randomUUID(),
  companyName: '',
  registrationNumber: '',
  dateOfIncorporation: EMPTY_DATE_PARTS,
  countryOfIncorporation: '',
  registeredAddress: INSTITUTION_ADDRESS_DEFAULTS,
  taxId: '',
  leiNumber: '',
  website: '',
  ownershipPct: undefined,
  typeOfBusiness: undefined,
  typeOfBusinessOther: '',
});

const institutionCorporateOwnerToInstitutionDetailsCorporateOwnerApi = ({
  dateOfIncorporation,
  registeredAddress,
  leiNumber,
  website,
  typeOfBusinessOther,
  ...corporateOwner
}: InstitutionCorporateOwner) => ({
  ...corporateOwner,
  dateOfIncorporation: toDateStringFromParts(dateOfIncorporation),
  registeredAddress: institutionAddressToInstitutionDetailsAddressApi(registeredAddress),
  leiNumber: leiNumber || undefined,
  website: website || undefined,
  ...(corporateOwner.typeOfBusiness === BusinessType.OTHER && { typeOfBusinessOther }),
});

const institutionDetailsCorporateOwnerApiToInstitutionCorporateOwner = ({
  dateOfIncorporation,
  registeredAddress,
  leiNumber,
  website,
  ...corporateOwner
}: InstitutionDetailsV2CorporateOwner): InstitutionCorporateOwner => ({
  ...corporateOwner,
  dateOfIncorporation: toDatePartsFromString(dateOfIncorporation),
  registeredAddress: institutionDetailsAddressApiToInstitutionAddress(registeredAddress),
  leiNumber: leiNumber ?? '',
  website: website ?? '',
  typeOfBusinessOther: 'typeOfBusinessOther' in corporateOwner ? corporateOwner.typeOfBusinessOther : '',
});

const institutionDetailsManagementPersonApiToInstitutionManagementPerson = ({ roles, isPep, ...person }: InstitutionDetailsV2ManagementPerson): InstitutionManagementPerson => ({
  ...institutionDetailsPersonApiToInstitutionPerson(person),
  roles,
  isPep,
});

// Parsing drops answers left from another route, such as UBOs from before the company said it was listed, or corporate owners when it has none
export const institutionOwnershipToInstitutionDetailsOwnershipApi = ({
  regulatedMarketListing,
  listedCompany,
  ownershipType,
  bearerSharesPct,
  ownershipChart,
  hasCorporateOwners,
  corporateOwners,
  hasOwners,
  ubos,
  management,
}: InstitutionOwnership): Promise<InstitutionDetailsV2Ownership> =>
  institutionDetailsV2OwnershipSchema.parseAsync({
    regulatedMarketListing,
    listedCompany: { ...listedCompany, mic: absentIfBlank(listedCompany.mic), ticker: absentIfBlank(listedCompany.ticker), isin: absentIfBlank(listedCompany.isin) },
    ownershipType,
    bearerSharesPct,
    ownershipChart,
    hasCorporateOwners,
    corporateOwners: corporateOwners.map(institutionCorporateOwnerToInstitutionDetailsCorporateOwnerApi),
    ubos: ubos.map((ubo) =>
      hasOwners === 'YES'
        ? { ...institutionPersonToInstitutionDetailsPersonApi(ubo), type: BeneficialOwnerType.OWNER, ownershipPct: ubo.ownershipPct, isPep: ubo.isPep }
        : { ...institutionPersonToInstitutionDetailsPersonApi(ubo), type: BeneficialOwnerType.SENIOR_MANAGING_OFFICIAL, isPep: ubo.isPep },
    ),
    management: management.map(({ roles, isPep, ...person }) => ({ ...institutionPersonToInstitutionDetailsPersonApi(person), roles, isPep })),
  });

export const institutionDetailsOwnershipApiToInstitutionOwnership = (ownership: InstitutionDetailsV2Ownership): InstitutionOwnership => {
  const management = ownership.management.map(institutionDetailsManagementPersonApiToInstitutionManagementPerson);

  switch (ownership.regulatedMarketListing) {
    case RegulatedMarketListing.LISTED:
      return {
        ...INSTITUTION_OWNERSHIP_DEFAULTS,
        regulatedMarketListing: ownership.regulatedMarketListing,
        listedCompany: { ...INSTITUTION_LISTED_COMPANY_DEFAULTS, ...ownership.listedCompany },
        management,
      };
    case RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY:
      return {
        ...INSTITUTION_OWNERSHIP_DEFAULTS,
        regulatedMarketListing: ownership.regulatedMarketListing,
        listedCompany: { ...INSTITUTION_LISTED_COMPANY_DEFAULTS, ...ownership.listedCompany },
        ownershipChart: ownership.ownershipChart,
        management,
      };
    case RegulatedMarketListing.NONE:
      return {
        regulatedMarketListing: ownership.regulatedMarketListing,
        listedCompany: INSTITUTION_LISTED_COMPANY_DEFAULTS,
        ownershipType: ownership.ownershipType,
        bearerSharesPct: ownership.bearerSharesPct,
        ownershipChart: ownership.ownershipChart,
        hasCorporateOwners: ownership.hasCorporateOwners,
        corporateOwners: ownership.hasCorporateOwners === 'YES' ? ownership.corporateOwners.map(institutionDetailsCorporateOwnerApiToInstitutionCorporateOwner) : [],
        hasOwners: ownership.ubos[0]?.type === BeneficialOwnerType.SENIOR_MANAGING_OFFICIAL ? 'NO' : 'YES',
        ubos: ownership.ubos.map((ubo) => ({
          ...institutionDetailsPersonApiToInstitutionPerson(ubo),
          ownershipPct: ubo.type === BeneficialOwnerType.OWNER ? ubo.ownershipPct : undefined,
          isPep: ubo.isPep,
        })),
        management,
      };
  }
};
